import React, { useState } from "react";
import Papa from "papaparse";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Divider,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Modal,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { Header } from "../../Components/Header/Header";
import { callAPI, dateFormat, exportToCSV, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSButton from "../../Components/Button/WSButton";
import { useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import http from "../../commonFunctions/htttpClientNew";
import WSDataGridForDashboard, { IColumns } from "../../Components/DataGrid/WSDataGridForDashboard";
import CloseIcon from "@mui/icons-material/Close";
interface CsvRecord {
  supplierName: string;
  invoiceNo: string;
  invoiceDate: string;
  amount: string;
  chequeIssued: string;
  isHold: string;
}

const ImportBill: React.FC = () => {
  const [csvData, setCsvData] = useState<CsvRecord[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const { showMessage } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [skippedBillList, setSkippedBillList] = useState<any>([]);
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const [open, setOpen] = React.useState(false);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const convertToISO = (dateStr: string): string => {
    if (!dateStr) return "";

    // replace "/" with "-" so both formats work
    const normalized = dateStr.replace(/\//g, "-");

    const [day, month, year] = normalized.split("-").map(Number);
    if (!day || !month || !year) return "";

    const date = new Date(year, month - 1, day);
    return date.toISOString();
  };
  const columns: IColumns[] = [
    {
      header: "Supplier Name",
      fieldName: "supplierName",
      type: "default"
    },
    {
      header: "Invoice No",
      fieldName: "invoiceNo",
      type: "default"
    },
    {
      header: "Amount",
      fieldName: "amount",
      type: "currency"
    },
    {
      header: "Invoice Date",
      fieldName: "invoiceDate",
      type: "date"
    },
    // {
    //   header: "Due Date",
    //   fieldName: "dueDate",
    //   type: "date"
    // },


    {
      header: "Is Hold",
      fieldName: "isHold",
      type: "booleanToString",
      groupType: "boolean"
    },
    {
      header: "Is Cheque Issued",
      fieldName: "chequeIssued",
      type: "booleanToString",
      groupType: "boolean"
    },


  ];
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse<CsvRecord>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        let data = result.data as any[];

        data = data
          // ✅ remove empty rows (all values falsy or undefined)
          .filter(row => Object.values(row).some(val => val && String(val).trim() !== ""))
          .map((row) => ({
            supplierName: row.SupplierName?.trim(),
            invoiceNo: row.InvoiceNo?.trim(),
            invoiceDate: convertToISO(row.InvoiceDate?.trim()),
            amount: row.Amount ? Number(parseFloat(row.Amount).toFixed(2)) : 0,
            chequeIssued: toBoolean(row.ChequeIssued), // ✅ explicit boolean
            isHold: toBoolean(row.IsHold),             // ✅ explicit boolean
          }));
        setCsvData(data);
      },
    });

  };
  const toBoolean = (value?: string): boolean => {
    if (!value) return false;

    const normalized = value.trim().toLowerCase();

    return ["true", "yes", "1"].includes(normalized);
  };
  const handleBulkUpload = async () => {
    try {
      setIsLoading(true);
      const response: any = await http.post(`NewBill/bulk-insert`, csvData);
      if (response?.success) {
        const allBills = response.skippedBills.map(((item: any) => item.bill));
        
        setSkippedBillList(allBills)
        setTimeout(() => {
          setOpen(true)
        }, 10);
        showMessage(response?.message, "success");
      }
      else {
        showMessage(response?.message || 'Somwthing went wrong', 'error')
      }
      setCsvData([]);
      //navigate("/");
    } catch (err: any) {
      console.error(err);
      showMessage(err?.message || "Upload failed!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = process.env.PUBLIC_URL + "/Files/PURCHASE_DATA_METRO.csv";
    link.download = "PURCHASE_DATA_METRO.csv";
    link.click();
  };
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
  };
  return (<>
    <Box sx={{ p: 4, bgcolor: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Header
          type="simple"
          isDivider={true}
          title="CSV Import & Bulk Upload"
          id="ImportListHeader"
          headerSx={{ marginTop: 0 }}
        />

        <Tooltip title="Download the CSV">
          <IconButton color="primary" onClick={handleDownload}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Upload Section */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 3,
          mb: 4,
          border: "1px dashed #90caf9",
        }}
      >
        <CardContent sx={{ textAlign: "center" }}>
          <CloudUpload sx={{ fontSize: 50, color: "primary.main", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload your CSV file
          </Typography>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUpload />}
            sx={{ borderRadius: 2, px: 3, textTransform: "none" }}
          >
            Choose File
            <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
          </Button>

          {fileName && (
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Selected File: <b>{fileName}</b>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Table Preview */}
      {csvData.length > 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview Data
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#eef2f6" }}>
                    <TableCell>
                      <b>Supplier Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Invoice No</b>
                    </TableCell>
                    <TableCell>
                      <b>Invoice Date</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>Amount</b>
                    </TableCell>
                    <TableCell>
                      <b>Cheque Issued</b>
                    </TableCell>
                    <TableCell>
                      <b>Is Hold</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": { bgcolor: "#fafafa" },
                        }}
                      >
                        <TableCell>{row.supplierName}</TableCell>
                        <TableCell>{row.invoiceNo}</TableCell>
                        <TableCell>
                          {dateFormat(row.invoiceDate, "DD-MM-YYYY")}
                        </TableCell>
                        <TableCell align="right">{row.amount}</TableCell>
                        <TableCell>
                          {row.chequeIssued ? "Yes" : "No"}
                        </TableCell>
                        <TableCell>{row.isHold ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={csvData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ mt: 2 }}
            />

            <Box sx={{ textAlign: "right", mt: 3 }}>
              <WSButton
                loading={isLoading}
                sx={{ fontSize: "0.85rem!important", px: 3 }}
                id="btnSave"
                label="Bulk Upload"
                size="medium"
                onClick={handleBulkUpload}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </Box> <Modal
      open={open}
      onClose={() => {
        setOpen(false);
        navigate('/');
      }}
    >
      <Box sx={style}>
        {/* Action buttons at top-right */}
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            display: 'flex',
            gap: 1 // space between buttons
          }}
        >
          {/* Export CSV */}
          <IconButton
            color="primary"
            aria-label="export csv"
            onClick={() => exportToCSV(skippedBillList, 'SkippedBills')}
          >
            <DownloadIcon />
          </IconButton>

          {/* Close */}
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="h6" sx={{ pr: 5 }}>
          Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <WSDataGridForDashboard
          title="Skipped Bills"
          columns={columns}
       
          id="skippedBillGrid"
          data={skippedBillList}
        />
      </Box>
    </Modal>

  </>
  );
};

export default ImportBill;
